import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import * as logger from 'loglevel';
import { journeyTypeToLongValue, journeyTypeToShortValue } from '../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { annotateAndLogError } from '../../../helpers/error';
import {
  NOT_AVAILABLE,
  EMPTY_OBJECT,
  formatValueStringEmpty,
  formatValue,
  formatDate
} from './helpers';
import { useTracking } from './TrackingContext';
import { publish } from './pubsub';
import { formatter } from '@everymundo/airmodules-event-datalayer';
import { doRedirect } from './redirects';

export { TrackingProvider } from './TrackingContext';

type flightTypeOptions = {
  [key: string]: string;
};

const flightTypeMap: flightTypeOptions = {
  rt: 'ROUND_TRIP',
  ow: 'ONE_WAY'
};

interface QueryType {
  origin?: string;
  destination?: string;
  departure?: string;
  return?: string;
  flightType?: string;
  siteEdition?: string;
  language?: string;
  passengers?: Record<string, any>;
  currencyCode?: string;
  travelClass?: string;
  promoCode?: string;
  flexibleDates?: boolean;
  device?: string;
  extraInfo?: Record<string, any>;
  trackingParameters?: Record<string, any>;
}

export function useContainerProps({
  fareItemInteractionType = 'popup',
  integrationSettings = EMPTY_OBJECT,
  meta = EMPTY_OBJECT,
  pageContext = EMPTY_OBJECT,
  prePopulation = EMPTY_OBJECT,
  tracking = EMPTY_OBJECT,
  filters = EMPTY_OBJECT,
  secondaryFilters = EMPTY_OBJECT
}: $TSFixMe) {
  let flexibleDates: boolean | '';
  const newFilters = { ...filters, ...secondaryFilters };

  if (newFilters?.dateRangeSettings?.enableFlexibleDates === true) {
    flexibleDates = !!newFilters?.dateRangeSettings?.checkedFlexibleDates;
  } else {
    flexibleDates = tracking.flexibleDates || '';
  }

  return React.useMemo(() => {
    return {
      fareItemInteractionType,

      // Tracking props
      eventType: tracking.eventType,
      eventCategory: tracking.eventCategory,
      flexibleDates: flexibleDates,
      passengerCount: tracking.passengerCount,
      promoCode: tracking.promoCode,
      trackingConfig: tracking.trackingConfig,
      useMiles: Boolean(prePopulation?.redemptionUnit),

      // Prepopulation props
      airlineIataCode: prePopulation.airlineIataCode,
      flightType: prePopulation.flightType,
      departureDateStart: prePopulation.departure?.start ?? null,
      returnDateEnd: prePopulation.return?.end ?? null,
      language: prePopulation.languageCode,
      currencyCode: formatValueStringEmpty(prePopulation.outputCurrencies),
      fareClass: formatValueStringEmpty(prePopulation.travelClasses),
      journeyType: formatValueStringEmpty(prePopulation.flightType),
      forceAirline: prePopulation.forceAirline,

      // Page context
      deviceCategory: pageContext.deviceCategory,
      siteEdition: pageContext.siteEdition,
      provider: pageContext.provider ? pageContext.provider : '',
      url: pageContext.url,

      // Integration settings
      v2DeepLinkApiSettings: integrationSettings.v2DeepLinkApiSettings
        ? integrationSettings.v2DeepLinkApiSettings
        : null,
      integrationSettings,

      // Component metadata
      tagName: meta.tag,
      meta
    };
  }, [
    fareItemInteractionType,
    integrationSettings,
    meta,
    pageContext,
    prePopulation,
    tracking,
    flexibleDates
  ]);
}

/**
 * This function get params in url
 * @param {*} query
 */

const getQueryParams = (query: $TSFixMe) => {
  const vars = query.split('&');
  const queryString: $TSFixMe = {};
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    const key = decodeURIComponent(pair[0]);
    const value = decodeURIComponent(pair[1]);
    // If first entry with this name
    if (typeof queryString[key] === 'undefined') {
      queryString[key] = decodeURIComponent(value);
      // If second entry with this name
    } else if (typeof queryString[key] === 'string') {
      const arr = [queryString[key], decodeURIComponent(value)];
      queryString[key] = arr;
      // If third or later entry with this name
    } else {
      queryString[key].push(decodeURIComponent(value));
    }
  }
  return queryString;
};

/**
 * This function get params in url that contains in white list
 * @param {*} trackingParametersWhitelist
 */
function getUrlParameters(trackingParametersWhitelist: $TSFixMe) {
  if (isSSR()) {
    return {};
  }
  const trackingParameters: $TSFixMe = {};
  if (Array.isArray(trackingParametersWhitelist)) {
    const query =
      window.location.search && window.location.search.startsWith('?')
        ? window.location.search.substring(1)
        : window.location.search;
    const params = getQueryParams(query);
    Object.keys(params).map((key) => {
      if (trackingParametersWhitelist.includes(key) && params[key] !== '') {
        trackingParameters[key] = params[key];
      }
      return key;
    });
  }
  return trackingParameters;
}

function getStaticParameters(v2DeepLinkApiSettings: $TSFixMe) {
  let params: $TSFixMe = {};
  if (
    v2DeepLinkApiSettings &&
    typeof v2DeepLinkApiSettings === 'object' &&
    v2DeepLinkApiSettings.constructor === Object
  ) {
    params = getUrlParameters(v2DeepLinkApiSettings.trackingParametersWhitelist);
    if (
      v2DeepLinkApiSettings.params &&
      v2DeepLinkApiSettings.params.trackingParameters &&
      typeof v2DeepLinkApiSettings.params.trackingParameters === 'object' &&
      v2DeepLinkApiSettings.params.trackingParameters.constructor === Object
    ) {
      params = Object.assign({}, params, v2DeepLinkApiSettings.params.trackingParameters);
      Object.keys(params).map((tp) => {
        if (params[tp] === '') {
          delete params[tp];
        }
        return tp;
      });
    }
  }
  return params;
}

export function getPopupData({ container, fare, state }: $TSFixMe) {
  const { origin, destination, flightType, journeyType, currencyCode, travelClass } = state;
  const { airlineIataCode, passengerCount, v2DeepLinkApiSettings, useMiles } = container;

  let newFlightType = formatValueStringEmpty(flightType);
  if (
    (fare && fare.origin !== undefined && fare.dataType === undefined) ||
    (fare && fare.origin !== undefined && fare.dataType === 'DATA_API')
  ) {
    //exist fare data
    if (fare.journeyType) {
      newFlightType =
        flightTypeMap[fare.journeyType] === undefined
          ? fare.journeyType
          : flightTypeMap[fare.journeyType];
    } else {
      if (container.flightType !== undefined) {
        if (container.flightType !== null) {
          newFlightType = container.flightType;
        }
      }
    }
  } else if (fare && fare.dataType === 'EMPTY_FROM_API') {
    if (container.flightType !== undefined) {
      if (container.flightType !== null) {
        newFlightType = container.flightType;
      }
    }
  }

  const data: $TSFixMe = {
    dataType: formatValueStringEmpty(fare.dataType),
    trackingParameters: getStaticParameters(v2DeepLinkApiSettings),
    passengers: formatValueStringEmpty(passengerCount),
    origin,
    destination,
    travelClass: formatValueStringEmpty(travelClass),
    currencyCode: formatValueStringEmpty(currencyCode),
    journeyType: formatValueStringEmpty(journeyType),
    flightType: newFlightType,
    airlineCode: formatValueStringEmpty(airlineIataCode),
    farenetTravelClass: formatValueStringEmpty(fare.farenetTravelClass || travelClass),
    useMiles: useMiles === undefined ? Boolean(fare.redemption) : useMiles,
    ...fare?.paramsTrackingExtra
  };

  if (state.originCity) {
    data.originCity = state.originCity;
  }
  if (state.destinationCity) {
    data.destinationCity = state.destinationCity;
  }

  if (fare.airlineCode) {
    data.airline = fare.airlineCode;
  }

  // Populate origin and destination from filters when component does not has data
  if (fare.dataType === 'EMPTY_FROM_API') {
    let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;
    const today = dayjs();
    if (!departureDate || departureDate.isBefore(today)) {
      departureDate = today;
    }
    let returnDate = fare.returnDate ? dayjs(fare.returnDate) : null;
    if (departureDate && !returnDate) {
      // If a return date is not defined an implicit return date of a week (7 days)
      // after the departure date is sent.
      returnDate = departureDate.add(7, 'day');
    }
    data.departureDate = formatDate(departureDate);
    data.returnDate = formatDate(returnDate);
    if (journeyType === 'ew' || journeyType === undefined) {
      data.journeyType = 'rt';
    }

    return data;
  }
  // Populate data when fare is not empty
  return {
    ...data,
    departureDate: formatValueStringEmpty(fare.departureDate),
    returnDate: formatValueStringEmpty(fare.returnDate),
    formattedDepartureDate: formatValueStringEmpty(fare.formattedDepartureDate),
    formattedReturnDate: formatValueStringEmpty(fare.formattedReturnDate),
    totalPrice: formatValueStringEmpty(fare.totalPrice),
    popularity: formatValueStringEmpty(fare.popularity),
    searchDate: formatValueStringEmpty(fare.searchDate),
    usdTotalPrice: formatValueStringEmpty(fare.usdTotalPrice),
    formattedTotalPrice: formatValueStringEmpty(fare.formattedTotalPrice),
    formattedUsdTotalPrice: formatValueStringEmpty(fare.formattedUsdTotalPrice),
    flightDeltaDays: formatValueStringEmpty(fare.flightDeltaDays)
  };
}

export function openPopup(fare: $TSFixMe) {
  let momentFormattedDeparture = null;
  let momentFormattedReturn = null;
  if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
    // eslint-disable-next-line
    const fcRuntime = window.EM.___fcRuntime;
    if (fcRuntime && fcRuntime.vendors && fcRuntime.vendors.moment) {
      const infameMomentPleaseDontImportMeNever = fcRuntime.vendors.moment;
      momentFormattedDeparture =
        fare && fare.departureDate && infameMomentPleaseDontImportMeNever(fare.departureDate);
      momentFormattedReturn =
        fare && fare.returnDate && infameMomentPleaseDontImportMeNever(fare.returnDate);
    } else {
      logger.warn(
        `INTEGRATION_ACTIONS: **OpenPopupAction** window.EM.___fcRuntime.vendors.moment not 
            available as global variable and the **Booking Popup** needed it`
      );
    }
  }
  const data = { ...fare };
  if (momentFormattedDeparture && momentFormattedDeparture.isValid()) {
    data.departureDate = momentFormattedDeparture;
  }
  if (momentFormattedReturn && momentFormattedReturn.isValid()) {
    data.returnDate = momentFormattedReturn;
  }

  publish('em.booking.open', data);
}

export function getPublisherData({ baseline, fare }: $TSFixMe) {
  const journeyTypeMap = journeyTypeToLongValue() as $TSFixMe;
  let data: $TSFixMe = {
    provider: baseline.provider,
    journeyType:
      fare.journeyType && journeyTypeMap[fare.journeyType]
        ? journeyTypeMap[fare.journeyType]
        : baseline.journeyType,
    originAirportIataCode: fare.origin,
    destinationAirportIataCode: fare.destination,
    route:
      fare.origin || fare.destination
        ? `${formatValueStringEmpty(fare.origin)}>${formatValueStringEmpty(fare.destination)}`
        : '',
    currencyCode: fare.currencyCode
      ? formatValueStringEmpty(fare.currencyCode)
      : baseline.currencyCode,
    totalPrice: formatValueStringEmpty(fare.formattedTotalPrice),
    totalPriceUSD: '',
    fareClass: fare.farenetTravelClass
      ? formatValueStringEmpty(fare.farenetTravelClass.toUpperCase())
      : baseline.fareClass,
    departureDate: fare.departureDate
      ? formatValueStringEmpty(fare.departureDate)
      : baseline.departureDate,
    returnDate: fare.returnDate ? fare.returnDate : baseline.returnDate,
    daysUntilFlight: '',
    tripLength: '',
    isFlexibleDates: '',
    discountCode: baseline.promoCode,
    deeplinkSiteEdition: '',
    miles: '',
    timestamp: baseline.timestamp,
    url: baseline.url,
    passenger: baseline.passenger,
    page: baseline.page,
    tagName: ''
  };

  if (fare.journeyType && data.journeyType === '') {
    data.journeyType = fare.journeyType;
  }

  if (fare.redemption !== undefined) {
    if (fare.redemption.unit.toUpperCase() === 'MILES') {
      data.miles = true;
      data.taxAmount = fare.redemption.taxAmount;
    }
  }

  if (fare.airlineCode) {
    data.airlineIataCode = fare.airlineCode;
  }
  if (baseline.tagName) {
    data.tagName = baseline.tagName;
  }
  return data;
}

function evaluateParameter(fare: $TSFixMe, param: $TSFixMe) {
  if (
    param &&
    (typeof param === 'string' || param instanceof String) &&
    param.indexOf('@@') !== -1
  ) {
    const paramEvaluation = param.split('.');

    if (paramEvaluation && paramEvaluation.length) {
      const paramsEvalLength = paramEvaluation[1].length;
      // fieldFare contain the field from api
      const fieldFare = paramEvaluation[1].substr(0, paramsEvalLength - 2);

      if (fare) {
        return fare[fieldFare];
      }
    }
  }

  return param;
}

function buildQuery(
  container: $TSFixMe,
  fare: $TSFixMe,
  origin: string,
  destination: string,
  airlines: boolean,
  state: $TSFixMe
) {
  const {
    deviceCategory,
    siteEdition,
    flightType,
    language,
    currencyCode,
    v2DeepLinkApiSettings: { params }
  } = container;

  // Get external platfom parameters
  const { travelClass, flexibleDates, promoCode, extraInfo } = params;
  const { v2DeepLinkApiSettings } = container;

  if (!deviceCategory) {
    logger.warn('INTEGRATION__DEEPLINK: **device** is not defined.');
  }
  if (!siteEdition) {
    logger.warn('INTEGRATION__DEEPLINK: **siteEdition** is not defined.');
  }
  if (!travelClass) {
    logger.warn('INTEGRATION__DEEPLINK: **travelClass** is not defined.');
  }
  if (promoCode === null) {
    logger.warn('INTEGRATION__DEEPLINK: **promoCode** is not defined.');
  }
  if (!extraInfo) {
    logger.warn('INTEGRATION__DEEPLINK: **extraInfo** is not defined.');
  }

  if (!fare) {
    logger.warn('INTEGRATION__DEEPLINK: **fare** is empty.');
  }

  // https://everymundo.atlassian.net/browse/FC-2908
  let newFlightType = flightType;
  if (
    (fare && fare.origin !== undefined && fare.dataType === undefined) ||
    (fare && fare.origin !== undefined && fare.dataType === 'DATA_API')
  ) {
    //exist fare data
    if (fare.journeyType) {
      newFlightType =
        flightTypeMap[fare.journeyType] === undefined
          ? fare.journeyType
          : flightTypeMap[fare.journeyType];
    } else {
      if (container.flightType !== undefined) {
        if (container.flightType !== null) {
          newFlightType = container.flightType;
        }
      }
    }
  } else if (fare && fare.dataType === 'EMPTY_FROM_API') {
    if (container.flightType !== undefined) {
      if (container.flightType !== null) {
        newFlightType = container.flightType;
      } else {
        newFlightType = 'ROUND_TRIP';
      }
    }
  }

  let newExtraInfo = { ...extraInfo };
  if (
    v2DeepLinkApiSettings?.includeLocationType === true &&
    fare?.originLocationType !== undefined &&
    fare?.originLocationType !== null &&
    fare?.destinationLocationType !== undefined &&
    fare?.destinationLocationType !== null
  ) {
    newExtraInfo['originLocationType'] = fare?.originLocationType;
    newExtraInfo['destinationLocationType'] = fare?.destinationLocationType;
  }

  let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;

  if (!departureDate) {
    departureDate = dayjs().add(container.departureDateStart, 'day');
  }
  let returnDate = fare.returnDate ? dayjs(fare.returnDate) : null;
  if (departureDate && !returnDate) {
    returnDate = departureDate.add(7, 'day');
  }

  let query: QueryType = {
    origin: (fare && fare.origin) || origin || '',
    destination: (fare && fare.destination) || destination || '',
    departure: formatDate(departureDate),
    return: fare && newFlightType === 'ROUND_TRIP' ? formatDate(returnDate) : '',
    flightType: newFlightType,
    siteEdition,
    language,
    passengers: {
      passenger1: 1
    },
    currencyCode: (fare && fare.currencyCode) || currencyCode || '',
    travelClass: fare && evaluateParameter(fare, travelClass),
    promoCode: fare && evaluateParameter(fare, promoCode),
    device: (deviceCategory && deviceCategory.toUpperCase()) || 'DESKTOP',
    extraInfo: newExtraInfo,
    trackingParameters: getStaticParameters(v2DeepLinkApiSettings)
  };

  const flexibleDatesValue =
    fare &&
    (state.dateRange.checkedFlexibleDates ??
      container.flexibleDates ??
      fare.flexibleDates ??
      evaluateParameter(fare, flexibleDates));

  if (flexibleDatesValue !== '') {
    query = { ...query, flexibleDates: flexibleDatesValue };
  }

  if (state.passengers && Array.isArray(state.passengers)) {
    query = {
      ...query,
      passengers: {
        passenger1: state.passengers[0],
        passenger2: state.passengers[1],
        passenger3: state.passengers[2],
        passenger4: state.passengers[3],
        passenger5: state.passengers[4],
        passenger6: state.passengers[5],
        passenger7: state.passengers[6]
      }
    };
  }
  if (airlines !== null && airlines !== undefined && origin !== '' && destination !== '') {
    const newQuery = { ...query, airlines: fare.airlineCode };
    return newQuery;
  }

  return query;
}

export function redirect({ container, fare, state }: $TSFixMe) {
  const { origin, destination, airlines } = state;
  const query = buildQuery(container, fare, origin, destination, airlines, state);
  const { v2DeepLinkApiSettings } = container;
  const newV2DeepLinkApiSettings = { ...v2DeepLinkApiSettings };
  let error = '';
  if (!newV2DeepLinkApiSettings.method) {
    error += 'INTEGRATION__DEEPLINK: **method** is not defined. ';
  }
  if (!newV2DeepLinkApiSettings.endPoint) {
    error += 'INTEGRATION__DEEPLINK: **endPoint** is not defined.';
  }
  if (
    newV2DeepLinkApiSettings.endPoint.search('%%airlineCode%%') !== -1 ||
    newV2DeepLinkApiSettings.endPoint.search('@@airlineIataCode@@') !== -1
  ) {
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '%%airlineCode%%',
      fare.airlineCode
    );
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '@@airlineIataCode@@',
      fare.airlineCode
    );
  }
  if (!newV2DeepLinkApiSettings.headers) {
    newV2DeepLinkApiSettings.headers = {};
  }
  if (error) {
    logger.error(error);
  } else {
    axios({
      method: newV2DeepLinkApiSettings.method,
      url: `${newV2DeepLinkApiSettings.endPoint}`,
      headers: { ...newV2DeepLinkApiSettings.headers },
      data: query
    })
      .then((response) => doRedirect(response.data))
      .catch((err) => {
        logger.error(
          annotateAndLogError(
            err,
            'INTEGRATION__DEEPLINK',
            false,
            false,
            container.integrationSettings,
            container.meta
          )
        );
      });
  }
}

function isEnableTrackingEvent(nameEvent: string, enableEventsName: Array<string>) {
  for (let index = 0; index < enableEventsName.length; index++) {
    const element = enableEventsName[index];
    if (element.toUpperCase() === nameEvent) {
      return true;
    }
  }
  return false;
}

function sendTrackingEvent(
  nameEvent: string,
  actionType: string,
  payload: any,
  enableEventsName: Array<string>,
  send: any
) {
  if (enableEventsName && enableEventsName.length !== 0) {
    if (isEnableTrackingEvent(actionType, enableEventsName)) {
      return send(nameEvent, payload);
    }
  } else {
    return send(nameEvent, payload);
  }
}

export function useTrackingChangeDatalayer() {
  const newContainer: $TSFixMe = useTracking();
  const { useMiles } = newContainer;
  const container = { ...newContainer };
  delete container.useMiles;
  const { eventType, fareItemInteractionType } = container;

  const {
    v2DeepLinkApiSettings: { params = {} }
  } = container;

  // Get external platfom parameters
  const { extraInfo } = params;

  const track = useCallback(
    (state: $TSFixMe, action: $TSFixMe, actualState: $TSFixMe) => {
      const getFlexibleDatesValue = () => {
        if (container.flexibleDates !== NOT_AVAILABLE) {
          if (actualState.dateRange.checkedFlexibleDates !== undefined) {
            return actualState.dateRange.checkedFlexibleDates;
          } else {
            return container.flexibleDates;
          }
        } else {
          return NOT_AVAILABLE;
        }
      };

      const baseline: $TSFixMe = {
        actionLabel: '',
        provider: formatValue(container.provider),
        airlineIataCode: formatValueStringEmpty(container.airlineIataCode).toUpperCase(),
        currencyCode: formatValueStringEmpty(container.currencyCode),
        departureDate: state.dateRange.start
          ? formatValueStringEmpty(formatDate(state.dateRange.start))
          : formatValueStringEmpty(formatDate(container.departureDateStart)),
        destinationAirportIataCode: formatValueStringEmpty(state.destination.value),
        fareClass: formatValueStringEmpty(actualState.travelClass),
        flexibleDates: formatValueStringEmpty(getFlexibleDatesValue()),
        journeyType: formatValueStringEmpty(state.flightType),
        originAirportIataCode: formatValueStringEmpty(state.origin.value),
        route:
          state.origin.value || state.destination.value
            ? `${formatValueStringEmpty(state.origin.value)}>${formatValueStringEmpty(
                state.destination.value
              )}`
            : '',
        promoCode: formatValueStringEmpty(container.promoCode),
        returnDate: formatValueStringEmpty(formatDate(state.dateRange.end)),
        deeplinkSiteEdition: container.siteEdition,
        flightStopCount: formatValueStringEmpty(actualState.flightStopCount),
        miles: '',
        timestamp: new Date(Date.now()).toISOString(),
        url: container.url ? container.url : '',
        daysUntilFlight: '',
        tripLength: '',
        passenger: [
          {
            count: formatValueStringEmpty(container.passengerCount),
            adultCount: formatValueStringEmpty(container.passengerCount),
            youngAdultCount: '',
            childCount: '',
            infantInLapCount: '',
            infantInSeatCount: ''
          }
        ],
        page: [
          {
            siteEdition: container.siteEdition,
            countryIsoCode: container.market,
            languageIsoCode: container.language
          }
        ]
      };

      if (extraInfo && extraInfo.useMiles) {
        baseline.miles = true;
      }

      // Tag name is optional
      if (container.tagName) {
        baseline.tagName = container.tagName;
      }

      const { enableCityCodes } = container.trackingConfig ? container.trackingConfig : false;

      let journeyTypeMap;
      let defaultTracking;
      let fareState;
      if (action.payload && action.payload.fare) {
        const { fare } = action.payload;
        journeyTypeMap = journeyTypeToShortValue() as $TSFixMe;

        let origin,
          destination = '';
        if (enableCityCodes && fare.aditionalOrigin !== '' && fare.aditionalDestination !== '') {
          origin = fare.aditionalOrigin;
          destination = fare.aditionalDestination;
        } else {
          origin = fare.origin ? fare.origin : state.origin.value;
          destination = fare.destination ? fare.destination : state.destination.value;
        }
        fareState = {
          ...state,
          destination: destination,
          origin: origin,
          journeyType: fare.journeyType ? fare.journeyType : journeyTypeMap[state.flightType],
          flightType: fare.flightType ? fare.flightType : state.flightType,
          currencyCode: fare.currencyCode
            ? fare.currencyCode
            : formatValueStringEmpty(container.currencyCode),
          travelClass: fare.travelClass
            ? fare.travelClass
            : formatValueStringEmpty(state.travelClass)
        };
        let adultCount = 0;
        let youngAdultCount = 0;
        let childCount = 0;
        let infantInLapCount = 0;
        let infantInSeatCount = 0;
        let passengerCount = formatValueStringEmpty(container.passengerCount);
        if (fareState.passengers && Array.isArray(fareState.passengers)) {
          passengerCount = 0;
          adultCount = fareState.passengers[0] + fareState.passengers[4];
          youngAdultCount = fareState.passengers[5] + fareState.passengers[6];
          childCount = fareState.passengers[1];
          infantInLapCount = fareState.passengers[2];
          infantInSeatCount = fareState.passengers[3];
          fareState.passengers.forEach((element: any) => {
            passengerCount += element;
          });
        }
        defaultTracking = {
          ...baseline,
          destinationAirportIataCode: formatValueStringEmpty(fareState.destination),
          originAirportIataCode: formatValueStringEmpty(fareState.origin),
          passengerCount: passengerCount,
          passenger: [
            {
              count: passengerCount > 0 ? passengerCount : '',
              adultCount: adultCount > 0 ? adultCount : '',
              youngAdultCount: youngAdultCount > 0 ? youngAdultCount : '',
              childCount: childCount > 0 ? childCount : '',
              infantInLapCount: infantInLapCount > 0 ? infantInLapCount : '',
              infantInSeatCount: infantInSeatCount > 0 ? infantInSeatCount : ''
            }
          ],
          route:
            fareState.origin || fareState.destination
              ? `${formatValueStringEmpty(fareState.origin)}>${formatValueStringEmpty(
                  fareState.destination
                )}`
              : ''
        };
      }
      const formatAirlines = (object: object) => {
        try {
          formatter.formatAirlines(object);
        } catch (error) {
          const MESSAGE = `TRACKING: **@everymundo/airmodules-event-datalayer** ${error}`;
          logger.error(MESSAGE);
        }
      };

      const send = (name: $TSFixMe, changes: $TSFixMe) => {
        // eslint-disable-next-line no-console
        console.log('OUTPUT FOR AIRLINES OBJECT', {
          module: eventType,
          event: name,
          eventAction: name,
          ...baseline,
          ...changes
        });

        formatAirlines({
          module: eventType,
          event: name,
          eventAction: name,
          ...baseline,
          ...changes
        });
      };

      const enableEventsName =
        container.trackingConfig !== undefined ? container.trackingConfig.enable : [];

      switch (action.type) {
        case 'ORIGIN_CHANGE': {
          if (state.histogramType !== null && state.histogramType !== undefined) {
            let date = {
              departureDate: NOT_AVAILABLE,
              returnDate: NOT_AVAILABLE
            };
            sendTrackingEvent(
              'change-origin',
              action.type,
              {
                originAirportIataCode: action.payload.value,
                ...date,
                actionLabel: action.payload.value
              },
              enableEventsName,
              send
            );
            return;
          }
          sendTrackingEvent(
            'change-origin',
            action.type,
            {
              originAirportIataCode: formatValueStringEmpty(action.payload.value),
              actionLabel: formatValueStringEmpty(action.payload.value)
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'ORIGIN_CLEAR':
        case 'ORIGIN_CLEAR_SECONDARY': {
          sendTrackingEvent(
            'change-origin',
            action.type,
            { originAirportIataCode: '', actionLabel: '' },
            enableEventsName,
            send
          );
          break;
        }
        case 'DESTINATION_CHANGE': {
          if (state.histogramType !== null && state.histogramType !== undefined) {
            let date = {
              departureDate: NOT_AVAILABLE,
              returnDate: NOT_AVAILABLE
            };
            sendTrackingEvent(
              'change-destination',
              action.type,
              {
                destinationAirportIataCode: action.payload.value,
                ...date,
                actionLabel: action.payload.value
              },
              enableEventsName,
              send
            );
            return;
          }
          sendTrackingEvent(
            'change-destination',
            action.type,
            {
              destinationAirportIataCode: formatValueStringEmpty(action.payload.value),
              actionLabel: action.payload.value
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DESTINATION_CLEAR':
        case 'DESTINATION_CLEAR_SECONDARY': {
          sendTrackingEvent(
            'change-destination',
            action.type,
            { destinationAirportIataCode: '', actionLabel: '' },
            enableEventsName,
            send
          );
          break;
        }
        case 'SWAP_LOCATIONS': {
          sendTrackingEvent(
            'change-origin',
            action.type,
            {
              originAirportIataCode: state.destination.value,
              destinationAirportIataCode: state.origin.value
            },
            enableEventsName,
            send
          );
          sendTrackingEvent(
            'change-destination',
            action.type,
            {
              originAirportIataCode: state.destination.value,
              destinationAirportIataCode: state.origin.value
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'SORTING_CHANGE': {
          sendTrackingEvent(
            'sort',
            action.type,
            { sortBy: action.payload.label },
            enableEventsName,
            send
          );
          break;
        }
        case 'BUDGET_CHANGE': {
          sendTrackingEvent(
            'change-budget',
            action.type,
            { budgetMaximumAmount: formatValueStringEmpty(action.payload), actionLabel: '' },
            enableEventsName,
            send
          );
          break;
        }
        case 'TRIP_DURATION_CHANGE': {
          sendTrackingEvent(
            'change-trip-length',
            action.type,
            { tripDuration: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'TRAVELER_CHANGE': {
          let selectedItems: $TSFixMe[] = [];

          Object.keys(action.payload).forEach((key) => {
            selectedItems = action.payload[key].selectedItems
              .filter((i: { value: string }) => i.value !== 'any')
              .map((i: any) => i);
          });
          sendTrackingEvent(
            'selected-travel-interest',
            action.type,
            { interest: selectedItems },
            enableEventsName,
            send
          );
          break;
        }
        case 'NUMBER_STOPS_CHANGE': {
          sendTrackingEvent(
            'select-stop',
            action.type,
            { flightStopCount: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'RATING_CHANGE': {
          sendTrackingEvent(
            'change-rating',
            action.type,
            { rating: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'FLIGHT_TYPE_CHANGE': {
          if (state.histogramType !== null && state.histogramType !== undefined) {
            let date = {
              departureDate: NOT_AVAILABLE,
              returnDate: NOT_AVAILABLE
            };
            sendTrackingEvent(
              'change-journey-type',
              action.type,
              { journeyType: action.payload, ...date },
              enableEventsName,
              send
            );
            return;
          }
          sendTrackingEvent(
            'change-journey-type',
            action.type,
            { journeyType: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'TRAVEL_CLASS_CHANGE': {
          sendTrackingEvent(
            'change-fare-class',
            action.type,
            { travelClass: action.payload, actionLabel: formatValueStringEmpty(action.payload) },
            enableEventsName,
            send
          );
          break;
        }
        case 'MONTH_CHANGE_HISTOGRAM': {
          let date = {
            departureDate: action.payload.dateRange.start
              ? formatDate(action.payload.dateRange.start)
              : NOT_AVAILABLE,
            returnDate: action.payload.dateRange.end
              ? formatDate(action.payload.dateRange.end)
              : NOT_AVAILABLE
          };
          sendTrackingEvent(
            'change-month',
            action.type,
            { month: dayjs(action.payload.start).month(), ...date },
            enableEventsName,
            send
          );
          break;
        }
        case 'MONTH_CHANGE': {
          sendTrackingEvent(
            'change-month',
            action.type,
            { month: dayjs(action.payload.start).month() },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_CHANGE_DEPARTURE': {
          const departureDate = formatDate(action.payload.dateRange.start);
          const returnDate = formatDate(action.payload.dateRange.end);
          const noChange = departureDate === returnDate;
          sendTrackingEvent(
            'change-departure-date',
            action.type,
            {
              departureDate,
              returnDate: noChange ? NOT_AVAILABLE : returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_CHANGE_RETURN': {
          const departureDate = formatDate(action.payload.dateRange.start);
          const returnDate = formatDate(action.payload.dateRange.end);
          sendTrackingEvent(
            'change-return-date',
            action.type,
            {
              departureDate,
              returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_SET_START_DATE': {
          const departureDate = formatDate(action.payload.dateRange.selected);
          const returnDate = NOT_AVAILABLE;
          sendTrackingEvent(
            'change-departure-date',
            action.type,
            {
              departureDate,
              returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_SET_SUGGESTED_DATE': {
          const departureDate = formatDate(action.payload.dateRange.start);
          const returnDate = formatDate(action.payload.dateRange.end);
          sendTrackingEvent(
            'change-return-date',
            action.type,
            {
              departureDate,
              returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_CHANGE': {
          const departureDate = formatDate(action.payload.start);
          const returnDate = formatDate(action.payload.end);

          const noChange = departureDate === returnDate;

          const type = action.payload.type ?? 'all';
          const isDeparture = type === 'all' || type === 'departure';
          const isReturn = type === 'all' || type === 'return';
          if (
            (isDeparture && departureDate !== baseline.departureDate) ||
            (baseline.flexibleDates !== '' &&
              state.dateRange.checkedFlexibleDates !== baseline.flexibleDates)
          ) {
            sendTrackingEvent(
              'change-departure-date',
              action.type,
              {
                departureDate,
                returnDate: noChange ? NOT_AVAILABLE : returnDate,
                flexibleDates: baseline.flexibleDates,
                actionLabel: departureDate
              },
              enableEventsName,
              send
            );
          }
          if (
            (isReturn && returnDate !== baseline.returnDate) ||
            (isReturn &&
              returnDate !== '' &&
              baseline.flexibleDates !== '' &&
              state.dateRange.checkedFlexibleDates !== baseline.flexibleDates)
          ) {
            sendTrackingEvent(
              'change-return-date',
              action.type,
              {
                departureDate,
                returnDate,
                flexibleDates: baseline.flexibleDates,
                actionLabel: returnDate
              },
              enableEventsName,
              send
            );
          }
          return undefined;
        }
        case 'LOAD_MORE': {
          sendTrackingEvent('load-more', action.type, {}, enableEventsName, send);
          break;
        }
        case 'CLEAR_ALL':
        case 'CLEAR_PARTIAL': {
          sendTrackingEvent(
            'reset-filter',
            action.type,
            {
              filters: 'cleared'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'MORE_DEALS': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            { actionLabel: 'more-deals' },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_ARROW_LEFT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'left-arrow'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_ARROW_RIGHT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'right-arrow'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_SWIPE_LEFT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'swipe-left'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_SWIPE_RIGHT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'swipe-right'
            },
            enableEventsName,
            send
          );
          break;
        }
        // case 'SEE_MORE': {
        //   const { fare } = action.payload;
        //   if (enableEventsName.length !== 0) {
        //     if (isEnableTrackingEvent(action.Type, enableEventsName)) {
        //       publish(
        //         `em.fare.book.${container.eventType}`,
        //         getPublisherData({
        //           baseline: { ...defaultTracking, useMiles: useMiles },
        //           fare,
        //           callToActionLabel: 'See-more'
        //         })
        //       );
        //     }
        //   } else {
        //     publish(
        //       `em.fare.book.${container.eventType}`,
        //       getPublisherData({
        //         baseline: { ...defaultTracking, useMiles: useMiles },
        //         fare,
        //         callToActionLabel: 'See-more'
        //       })
        //     );
        //   }
        //   return undefined;
        // }
        case 'OPEN_BOOKING': {
          const { fare, callToActionLabel } = action.payload;
          if (!fare) {
            return undefined;
          }
          if (typeof fare === 'object' && fare.constructor === Object) {
            // openPopup(
            //   getPopupData({ container: { ...container, useMiles }, state: fareState, fare })
            // );
            if (enableEventsName && enableEventsName.length !== 0) {
              if (isEnableTrackingEvent(action.type, enableEventsName)) {
                // eslint-disable-next-line no-console
                console.log('OUTPUT FOR AIRLINES OBJECT', {
                  module: eventType,
                  event: 'open-booking-popup',
                  eventAction: 'open-booking-popup',
                  ...getPublisherData({ baseline, fare }),
                  actionLabel: callToActionLabel
                });

                formatAirlines({
                  module: eventType,
                  event: 'open-booking-popup',
                  eventAction: 'open-booking-popup',
                  ...getPublisherData({ baseline, fare }),
                  actionLabel: callToActionLabel
                });
              } else {
                // eslint-disable-next-line no-console
                console.log('OUTPUT FOR AIRLINES OBJECT', {
                  module: eventType,
                  event: 'open-booking-popup',
                  eventAction: 'open-booking-popup',
                  ...getPublisherData({ baseline, fare }),
                  actionLabel: callToActionLabel
                });

                formatAirlines({
                  module: eventType,
                  event: 'open-booking-popup',
                  eventAction: 'open-booking-popup',
                  ...getPublisherData({ baseline, fare }),
                  actionLabel: callToActionLabel
                });
              }
            }
          }

          break;
        }
        case 'FARE_CLICK': {
          // const { fare, callToActionLabel } = action.payload;
          const { fare } = action.payload;
          if (!fare) {
            return undefined;
          }
          if (typeof fare === 'object' && fare.constructor === Object) {
            if (fareItemInteractionType === 'deeplink') {
              if (enableEventsName && enableEventsName.length !== 0) {
                if (isEnableTrackingEvent(action.type, enableEventsName)) {
                  // eslint-disable-next-line no-console
                  console.log('OUTPUT FOR AIRLINES OBJECT', {
                    event: 'fsi',
                    module: eventType,
                    eventAction: 'fsi',
                    ...getPublisherData({ baseline: defaultTracking, fare }),
                    actionLabel: 'book-now'
                  });

                  formatAirlines({
                    event: 'fsi',
                    module: eventType,
                    eventAction: 'fsi',
                    ...getPublisherData({ baseline: defaultTracking, fare }),
                    actionLabel: 'book-now'
                  });
                }
              } else {
                // eslint-disable-next-line no-console
                console.log('OUTPUT FOR AIRLINES OBJECT', {
                  event: 'fsi',
                  module: eventType,
                  eventAction: 'fsi',
                  ...getPublisherData({ baseline: defaultTracking, fare }),
                  actionLabel: 'book-now'
                });

                formatAirlines({
                  event: 'fsi',
                  module: eventType,
                  eventAction: 'fsi',
                  ...getPublisherData({ baseline: defaultTracking, fare }),
                  actionLabel: 'book-now'
                });
              }
              redirect({ container, state: fareState, fare });
            }
            if (fareItemInteractionType === 'popup') {
              openPopup(
                getPopupData({ container: { ...container, useMiles }, state: fareState, fare })
              );
              if (enableEventsName && enableEventsName.length !== 0) {
                if (isEnableTrackingEvent(action.type, enableEventsName)) {
                  // eslint-disable-next-line no-console
                  console.log('OUTPUT FOR AIRLINES OBJECT', {
                    module: eventType,
                    event: 'open-booking-popup',
                    eventAction: 'open-booking-popup',
                    ...defaultTracking,
                    actionLabel: 'book-now'
                  });

                  formatAirlines({
                    module: eventType,
                    event: 'open-booking-popup',
                    eventAction: 'open-booking-popup',
                    ...defaultTracking,
                    actionLabel: 'book-now'
                  });
                } else {
                  // eslint-disable-next-line no-console
                  console.log('OUTPUT FOR AIRLINES OBJECT', {
                    module: eventType,
                    event: 'open-booking-popup',
                    eventAction: 'open-booking-popup',
                    ...defaultTracking,
                    actionLabel: 'book-now'
                  });

                  formatAirlines({
                    module: eventType,
                    event: 'open-booking-popup',
                    eventAction: 'open-booking-popup',
                    ...defaultTracking,
                    actionLabel: 'book-now'
                  });
                }
              }
            }
          }
          return undefined;
        }
        case 'SELECT_MAP_DESTINATION': {
          sendTrackingEvent(
            'select-map-destination',
            action.type,
            {
              destinationAirportIataCode: action.payload.value,
              departureDate:
                action.payload.start !== undefined ? action.payload.start : NOT_AVAILABLE,
              returnDate: action.payload.end !== undefined ? action.payload.end : NOT_AVAILABLE
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'EXPAND_MAP': {
          sendTrackingEvent(
            'expand-map',
            action.type,
            { expanded: action.payload.value },
            enableEventsName,
            send
          );
          break;
        }
        case 'ZOOM': {
          const { oldZoom, newZoom, expanded } = action.payload;
          sendTrackingEvent(
            'zoom',
            action.type,
            { expanded, zoom: oldZoom > newZoom ? 'out' : 'in' },
            enableEventsName,
            send
          );
          break;
        }
        case 'TOGGLE_FARE_LIST': {
          sendTrackingEvent(
            'toggle-farelist',
            action.type,
            { action: action.payload.event },
            enableEventsName,
            send
          );
          break;
        }
        case 'AIRLINES_CHANGE': {
          sendTrackingEvent(
            'airlineCodes',
            action.type,
            { airlineCodes: action.payload.value },
            enableEventsName,
            send
          );
          break;
        }
        default: {
          return undefined;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newContainer, extraInfo, eventType, fareItemInteractionType]
  );

  return track;
}
